import React, { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import SelectMenu from './SelectMenu';

/**
 * AddressComposite — zip-first address field used app-wide (LAT-25, 29).
 *
 * Field order is fixed: ZIP → (auto-fill City + State) → Street → Unit
 * The exact same component is used on:
 *   - Onboarding (Company address)
 *   - Add/Edit Customer
 *   - Quote site address
 *   - Job site address
 *
 * State-by-zip lookup uses a lightweight built-in prefix table for the
 * 50 US states (no network call, instant). Cities can't be derived from
 * ZIP alone without a real DB, so City stays user-editable; if a parent
 * supplies `onZipLookup(zip)`, the returned `{city, state}` will be used
 * to auto-fill BOTH fields.
 *
 * Value shape:
 *   { zip, city, state, line1, line2 }
 *
 * Validation:
 *   - All fields optional by default. Pass `required` to mark the whole
 *     composite as required (zip + state become required for submit).
 *
 * Accessibility:
 *   - Each input has its own label and id (auto-generated if not given).
 *   - Tab order follows visual order: ZIP → State → City → Street → Unit.
 */

const US_STATES = [
  ['AL','Alabama'],['AK','Alaska'],['AZ','Arizona'],['AR','Arkansas'],
  ['CA','California'],['CO','Colorado'],['CT','Connecticut'],['DE','Delaware'],
  ['FL','Florida'],['GA','Georgia'],['HI','Hawaii'],['ID','Idaho'],
  ['IL','Illinois'],['IN','Indiana'],['IA','Iowa'],['KS','Kansas'],
  ['KY','Kentucky'],['LA','Louisiana'],['ME','Maine'],['MD','Maryland'],
  ['MA','Massachusetts'],['MI','Michigan'],['MN','Minnesota'],['MS','Mississippi'],
  ['MO','Missouri'],['MT','Montana'],['NE','Nebraska'],['NV','Nevada'],
  ['NH','New Hampshire'],['NJ','New Jersey'],['NM','New Mexico'],['NY','New York'],
  ['NC','North Carolina'],['ND','North Dakota'],['OH','Ohio'],['OK','Oklahoma'],
  ['OR','Oregon'],['PA','Pennsylvania'],['RI','Rhode Island'],['SC','South Carolina'],
  ['SD','South Dakota'],['TN','Tennessee'],['TX','Texas'],['UT','Utah'],
  ['VT','Vermont'],['VA','Virginia'],['WA','Washington'],['WV','West Virginia'],
  ['WI','Wisconsin'],['WY','Wyoming'],['DC','District of Columbia'],
];

// IDI-655: exported for onboarding's Sales Tax by State picker.
export const STATE_OPTIONS = US_STATES.map(([v, l]) => ({ value: v, label: `${v} — ${l}` }));

/**
 * Quick zip-prefix → state lookup. This is best-effort (US ZIP → state
 * mapping is non-trivial at the boundary), but it's correct for the
 * overwhelming majority of well-known prefixes. Specific overrides win.
 */
export function stateFromZip(zip) {
  if (!zip || zip.length < 3) return null;
  const n = parseInt(zip.slice(0, 3), 10);
  if (Number.isNaN(n)) return null;
  // Coarse mapping by first 3 digits (USPS sectional center facility ranges)
  if (n >= 0 && n <= 4) return null;
  if (n >= 5 && n <= 27) return 'MA';
  if (n >= 28 && n <= 29) return 'RI';
  if (n >= 30 && n <= 38) return 'NH';
  if (n >= 39 && n <= 49) return 'ME';
  if (n >= 50 && n <= 54) return 'VT';
  if (n >= 55 && n <= 59) return 'MA';
  if (n >= 60 && n <= 69) return 'CT';
  if (n >= 70 && n <= 89) return 'NJ';
  if (n >= 100 && n <= 149) return 'NY';
  if (n >= 150 && n <= 196) return 'PA';
  if (n >= 197 && n <= 199) return 'DE';
  if (n >= 200 && n <= 205) return 'DC';
  if (n >= 206 && n <= 219) return 'MD';
  if (n >= 220 && n <= 246) return 'VA';
  if (n >= 247 && n <= 268) return 'WV';
  if (n >= 270 && n <= 289) return 'NC';
  if (n >= 290 && n <= 299) return 'SC';
  if (n >= 300 && n <= 319) return 'GA';
  if (n >= 320 && n <= 349) return 'FL';
  if (n >= 350 && n <= 369) return 'AL';
  if (n >= 370 && n <= 385) return 'TN';
  if (n >= 386 && n <= 397) return 'MS';
  if (n >= 398 && n <= 399) return 'GA';
  if (n >= 400 && n <= 427) return 'KY';
  if (n >= 430 && n <= 458) return 'OH';
  if (n >= 460 && n <= 479) return 'IN';
  if (n >= 480 && n <= 499) return 'MI';
  if (n >= 500 && n <= 528) return 'IA';
  if (n >= 530 && n <= 549) return 'WI';
  if (n >= 550 && n <= 567) return 'MN';
  if (n >= 570 && n <= 577) return 'SD';
  if (n >= 580 && n <= 588) return 'ND';
  if (n >= 590 && n <= 599) return 'MT';
  if (n >= 600 && n <= 629) return 'IL';
  if (n >= 630 && n <= 658) return 'MO';
  if (n >= 660 && n <= 679) return 'KS';
  if (n >= 680 && n <= 693) return 'NE';
  if (n >= 700 && n <= 714) return 'LA';
  if (n >= 716 && n <= 729) return 'AR';
  if (n >= 730 && n <= 749) return 'OK';
  if (n >= 750 && n <= 799) return 'TX';
  if (n >= 800 && n <= 816) return 'CO';
  if (n >= 820 && n <= 831) return 'WY';
  if (n >= 832 && n <= 838) return 'ID';
  if (n >= 840 && n <= 847) return 'UT';
  if (n >= 850 && n <= 865) return 'AZ';
  if (n >= 870 && n <= 884) return 'NM';
  if (n >= 889 && n <= 898) return 'NV';
  if (n >= 900 && n <= 961) return 'CA';
  if (n >= 967 && n <= 968) return 'HI';
  if (n >= 970 && n <= 979) return 'OR';
  if (n >= 980 && n <= 994) return 'WA';
  if (n >= 995 && n <= 999) return 'AK';
  return null;
}

const EMPTY = { zip: '', city: '', state: '', line1: '', line2: '' };

export default function AddressComposite({
  value = EMPTY,
  onChange,
  onZipLookup,            // optional async (zip) => { city, state }
  required = false,
  disabled = false,
  errors = {},            // { zip, state, city, line1 } — any truthy = invalid
  idPrefix = 'addr',
  className,
  showLine2 = true,
}) {
  const v = { ...EMPTY, ...(value || {}) };
  const lastZipLookup = useRef('');
  const [lookingUp, setLookingUp] = useState(false);

  // Stable refs to the LATEST value + onChange so the async zip lookup
  // below doesn't capture stale closures (which would clobber concurrent
  // typing in city/state if the lookup resolves slowly).
  const valueRef = useRef(v);
  valueRef.current = v;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  function patch(p) {
    onChange?.({ ...v, ...p });
  }

  // Auto-fill city/state when zip reaches 5 digits
  useEffect(() => {
    const zip = (v.zip || '').replace(/\D/g, '');
    if (zip.length !== 5) return;
    if (lastZipLookup.current === zip) return;
    lastZipLookup.current = zip;

    let cancelled = false;
    (async () => {
      // Always fill state from prefix table immediately
      const stateGuess = stateFromZip(zip);
      const updates = {};
      const current = valueRef.current;
      if (stateGuess && !current.state) updates.state = stateGuess;

      // Optional async lookup for city
      if (onZipLookup) {
        try {
          setLookingUp(true);
          const res = await onZipLookup(zip);
          if (!cancelled && res) {
            const latest = valueRef.current;
            // Don't overwrite anything the user has typed since we started
            if (res.state && !latest.state) updates.state = res.state;
            if (res.city  && !latest.city)  updates.city  = res.city;
          }
        } catch {
          /* silent fail — user can still type city */
        } finally {
          if (!cancelled) setLookingUp(false);
        }
      }

      if (Object.keys(updates).length > 0 && !cancelled) {
        const latest = valueRef.current;
        onChangeRef.current?.({ ...latest, ...updates });
      }
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [v.zip]);

  return (
    <fieldset
      className={clsx('grid gap-3', className)}
      disabled={disabled}
      style={{ border: 0, padding: 0, margin: 0 }}
    >
      <div className="grid grid-cols-12 gap-3">
        {/* ZIP — first */}
        <Field
          label="ZIP code"
          required={required}
          error={errors.zip}
          className="col-span-4"
          htmlFor={`${idPrefix}-zip`}
          hint={lookingUp ? 'Looking up…' : null}
        >
          <input
            id={`${idPrefix}-zip`}
            type="text"
            inputMode="numeric"
            autoComplete="postal-code"
            maxLength={10}
            value={v.zip}
            onChange={(e) => patch({ zip: e.target.value })}
            placeholder="12345"
            className="bidit-input"
          />
        </Field>

        {/* State — auto-filled but editable */}
        <Field
          label="State"
          required={required}
          error={errors.state}
          className="col-span-3"
          htmlFor={`${idPrefix}-state`}
        >
          <SelectMenu
            id={`${idPrefix}-state`}
            value={v.state || null}
            onChange={(val) => patch({ state: val || '' })}
            options={STATE_OPTIONS}
            placeholder="—"
            width="full"
            invalid={!!errors.state}
          />
        </Field>

        {/* City — auto-fill if onZipLookup provided, else free entry */}
        <Field
          label="City"
          error={errors.city}
          className="col-span-5"
          htmlFor={`${idPrefix}-city`}
        >
          <input
            id={`${idPrefix}-city`}
            type="text"
            autoComplete="address-level2"
            value={v.city}
            onChange={(e) => patch({ city: e.target.value })}
            placeholder="City"
            className="bidit-input"
          />
        </Field>
      </div>

      <div className="grid grid-cols-12 gap-3">
        <Field
          label="Street address"
          error={errors.line1}
          className={showLine2 ? 'col-span-8' : 'col-span-12'}
          htmlFor={`${idPrefix}-line1`}
        >
          <input
            id={`${idPrefix}-line1`}
            type="text"
            autoComplete="address-line1"
            value={v.line1}
            onChange={(e) => patch({ line1: e.target.value })}
            placeholder="123 Main St"
            className="bidit-input"
          />
        </Field>

        {showLine2 && (
          <Field
            label="Apt / Suite"
            className="col-span-4"
            htmlFor={`${idPrefix}-line2`}
          >
            <input
              id={`${idPrefix}-line2`}
              type="text"
              autoComplete="address-line2"
              value={v.line2}
              onChange={(e) => patch({ line2: e.target.value })}
              placeholder="Apt 4B"
              className="bidit-input"
            />
          </Field>
        )}
      </div>
    </fieldset>
  );
}

function Field({ label, htmlFor, required, error, hint, children, className }) {
  return (
    <div className={className}>
      <label
        htmlFor={htmlFor}
        className="block text-xs font-medium mb-1"
        style={{ color: 'var(--color-text-default)' }}
      >
        {label} {required && <span style={{ color: 'var(--color-error)' }}>*</span>}
      </label>
      {children}
      {error && (
        <p
          className="mt-1 text-xs"
          style={{ color: 'var(--color-error)' }}
        >
          {error}
        </p>
      )}
      {hint && !error && (
        <p
          className="mt-1 text-xs"
          style={{ color: 'var(--color-text-muted)' }}
        >
          {hint}
        </p>
      )}
    </div>
  );
}
